<script setup lang="ts">
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { InputNumber } from "@/components/episto-ui/input-number";
  import { Label } from "@/components/ui/label";
  import { Tooltip } from "@/components/ui/tooltip";
  import { useQuotasStore } from "@stores/survey/quotas";
  import { useSurveyTargetsStore } from "@stores/survey/surveyTargets";

  const props = defineProps<{ index: number }>();

  const quotasStore = useQuotasStore();
  const { answerTitle, isMultiAnswerQuota } = quotasStore;
  const { currentQuotas } = storeToRefs(quotasStore);
  const { selectedTarget } = storeToRefs(useSurveyTargetsStore());

  const quota = computed(() => currentQuotas.value[props.index]);
  const isPercentage = computed(() => quota.value.unit === "percentage");

  const total = computed(() =>
    quota.value.answer_quotas.reduce((sum, answerQuota) => sum + (Number(answerQuota.target_value) || 0), 0),
  );

  const limit = computed(() => (isPercentage.value ? 100 : Number(selectedTarget.value?.target) || 0));
  const hasLimit = computed(() => limit.value > 0 && !isMultiAnswerQuota(quota.value));
  const isOverLimit = computed(() => hasLimit.value && total.value > limit.value);

  const round = (value: number) => Math.round(value * 100) / 100;
  const totalLabel = computed(() =>
    isPercentage.value ? `${round(total.value)}%` : `${round(total.value)}${hasLimit.value ? ` / ${limit.value}` : ""}`,
  );
  const gapLabel = computed(() => {
    if (!hasLimit.value) return "";
    const gap = round(limit.value - total.value);
    if (gap === 0) return "complete";
    const unit = isPercentage.value ? "%" : "";
    return gap > 0 ? `${gap}${unit} left` : `${-gap}${unit} over`;
  });
</script>

<template>
  <div class="mt-4 space-y-3">
    <div class="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg bg-muted px-3 py-2">
      <span class="text-13 text-primary">Default tolerance</span>
      <Label class="text-xs font-normal text-muted-foreground">Min</Label>
      <div class="w-16">
        <InputNumber
          v-model="quota.global_min_tolerance"
          :min="0"
          :max="100"
          :steppers="false"
          class="h-8 bg-background text-right" />
      </div>
      <Label class="text-xs font-normal text-muted-foreground">Max</Label>
      <div class="w-16">
        <InputNumber
          v-model="quota.global_max_tolerance"
          :min="0"
          :max="100"
          :steppers="false"
          class="h-8 bg-background text-right" />
      </div>
      <span class="text-xs text-muted-foreground">%</span>
    </div>

    <div>
      <div class="grid grid-cols-24 gap-2 border-b pb-1.5">
        <span class="heading-label col-span-11">Answer</span>
        <span class="heading-label col-span-5 text-right">Target{{ isPercentage ? " (%)" : "" }}</span>
        <span class="heading-label col-span-4 text-right">Tol. min</span>
        <span class="heading-label col-span-4 text-right">Tol. max</span>
      </div>

      <div
        v-for="answerQuota in quota.answer_quotas"
        :key="answerQuota.id ?? answerQuota.answer"
        class="grid grid-cols-24 items-center gap-2 py-1">
        <div class="col-span-11 min-w-0">
          <Tooltip :content="answerTitle(quota.card_token, answerQuota.answer)">
            <span class="block min-w-0 truncate text-13 text-foreground">
              {{ answerTitle(quota.card_token, answerQuota.answer) }}
            </span>
          </Tooltip>
        </div>
        <div class="col-span-5">
          <InputNumber
            v-model="answerQuota.target_value"
            class="h-8 text-right"
            :min="0"
            :max="isPercentage ? 100 : undefined"
            :steppers="false" />
        </div>
        <div class="col-span-4">
          <InputNumber
            v-model="answerQuota.tolerance_min_percent"
            class="h-8 text-right"
            :min="0"
            :max="100"
            :steppers="false"
            :placeholder="quota.global_min_tolerance?.toString()" />
        </div>
        <div class="col-span-4">
          <InputNumber
            v-model="answerQuota.tolerance_max_percent"
            class="h-8 text-right"
            :min="0"
            :max="100"
            :steppers="false"
            :placeholder="quota.global_max_tolerance?.toString()" />
        </div>
      </div>

      <div class="grid grid-cols-24 items-baseline gap-2 border-t pt-2">
        <div class="col-span-11 flex items-baseline gap-2">
          <span class="text-13 font-medium text-primary">Total</span>
          <span
            v-if="gapLabel"
            class="text-xs"
            :class="isOverLimit ? 'text-destructive' : 'text-muted-foreground'">
            {{ gapLabel }}
          </span>
        </div>
        <span
          class="col-span-5 pr-3 text-right text-13 font-semibold"
          :class="isOverLimit ? 'text-destructive' : 'text-primary'">
          {{ totalLabel }}
        </span>
      </div>
    </div>
  </div>
</template>
